import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import Dialog from "../Dialog";
import Button from "../Button";
import { sendEmailOtp, verifyEmailOtp } from "../../Utils/apiServices";

/**
 * Verifies ownership of an email address via a 6-digit OTP.
 * On open it sends a code to `email`; on successful verification it calls onVerified().
 */
const EmailOtpDialog = ({ isOpen, email, onClose, onVerified }) => {
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const sendCode = async (targetEmail) => {
    setError("");
    setInfo("");
    setSending(true);
    try {
      const sendRes = await sendEmailOtp({ email: targetEmail });
      const otpSent = sendRes?.success && sendRes?.data?.success !== false;
      if (!otpSent) {
        setError(sendRes?.data?.message || sendRes?.error || "Unable to send the verification code. Please try again.");
      } else {
        setInfo(`We sent a 6-digit code to ${targetEmail}.`);
      }
    } catch (err) {
      setError(err?.error || "Unable to send the verification code. Please try again.");
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (isOpen && email) {
      setCode("");
      setError("");
      setInfo("");
      sendCode(email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, email]);

  const handleVerify = async () => {
    const trimmed = (code || "").trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setError("Please enter the 6-digit verification code.");
      return;
    }
    setVerifying(true);
    setError("");
    try {
      const verifyRes = await verifyEmailOtp({ email, otp_code: trimmed });
      const verified = verifyRes?.success && verifyRes?.data?.success === true;
      if (!verified) {
        setError(verifyRes?.data?.message || verifyRes?.error || "OTP verification failed. Please try again.");
        return;
      }
      onVerified();
    } catch (err) {
      setError(err?.error || "OTP verification failed. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  const busy = sending || verifying;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={busy ? () => {} : onClose}
      title="Verify your new email"
      description="Confirm the new address before we update your account."
      maxWidth="max-w-md"
    >
      <div className="flex flex-col gap-5">
        <div className="flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
          <div className="h-10 w-10 shrink-0 inline-flex items-center justify-center rounded-lg bg-blue-500 shadow-sm">
            <Icon icon="lucide:mail-check" className="h-5 w-5 text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-blue-700">Verifying</p>
            <p className="text-sm font-semibold text-slate-900 truncate">{email}</p>
          </div>
        </div>

        {info && !error && (
          <p className="text-sm text-slate-600">{info}</p>
        )}

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
            <Icon icon="lucide:alert-circle" className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-slate-700 text-left">
            Verification code
          </label>
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={code}
            onChange={(e) => {
              setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
              if (error) setError("");
            }}
            placeholder="123456"
            disabled={busy}
            className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-center text-lg tracking-[0.5em] font-semibold text-slate-900 shadow-sm placeholder:tracking-normal placeholder:text-slate-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue disabled:bg-slate-100"
          />
          <button
            type="button"
            onClick={() => sendCode(email)}
            disabled={busy}
            className="self-start text-sm font-medium text-brand-blue hover:underline disabled:text-slate-400 disabled:no-underline"
          >
            {sending ? "Sending..." : "Resend code"}
          </button>
        </div>

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-200">
          <Button
            variant="secondary"
            size="medium"
            onClick={onClose}
            disabled={busy}
            className="bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="medium"
            onClick={handleVerify}
            disabled={busy || code.length !== 6}
            startIcon={
              verifying ? (
                <Icon icon="svg-spinners:ring-resize" className="h-4 w-4" />
              ) : (
                <Icon icon="lucide:shield-check" className="h-4 w-4" />
              )
            }
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            {verifying ? "Verifying..." : "Verify & save"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default EmailOtpDialog;
